import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Table, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetVwQaqcPipeMaterials } from '../../hooks/genericHooks';
type VwQaqcPipeMaterialsRow = {
  itemcode: string;
  size_text: string;
  description: string | null;
};

export type PipeMaterialSelectMode = 'basic' | 'combined';
export type PipeMaterialCombined = { itemcode: string; size_text: string; description: string | null };

export interface PipeMaterialSelectProps {
  open: boolean;
  mode?: PipeMaterialSelectMode; // 'basic': return single itemcode; 'combined': return array of {itemcode,size_text,description}
  title?: string;
  onOk: (value: string | PipeMaterialCombined[]) => void;
  onCancel: () => void;
  initialItemcode?: string; // optional: preselect by aligning filters to the item's attributes
}

const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

const PipeMaterialSelect: React.FC<PipeMaterialSelectProps> = ({ open, mode = 'basic', title, onOk, onCancel, initialItemcode }) => {
  const { data = [], isLoading } = useGetVwQaqcPipeMaterials() as any;
  const rows: VwQaqcPipeMaterialsRow[] = Array.isArray(data) ? data : (data?.data ?? []);

  const [sizeFilter, setSizeFilter] = useState<string | undefined>(undefined);
  const [descFilter, setDescFilter] = useState<string | undefined>(undefined);
  // For "search as you type" on description without requiring selection
  const [descSearch, setDescSearch] = useState<string>('');
  // Invisible itemcode filter driven by initialItemcode
  const [itemcodeFilter, setItemcodeFilter] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pageSize, setPageSize] = useState<number>(20);

  const filtered = useMemo(() => {
    const base = (rows || [])
      .filter(r => (!itemcodeFilter || norm(r.itemcode) === norm(itemcodeFilter)))
      .filter(r => (!sizeFilter || norm(r.size_text) === norm(sizeFilter)))
      .filter(r => (!descFilter || norm(r.description) === norm(descFilter)));
    // Apply live description search only when a specific description value isn't selected
    const s = (descSearch || '').trim().toLowerCase();
    if (!descFilter && s) {
      const terms = s.split(/\s+/).filter(Boolean);
      return base.filter(r => {
        const val = (r.description ?? '').toLowerCase();
        return terms.every(t => val.includes(t));
      });
    }
    return base;
  }, [rows, sizeFilter, descFilter, descSearch]);

  // Align filters to existing value when opening (best-effort) and set invisible itemcode filter
  useEffect(() => {
    if (!open || !initialItemcode) return;
    const match = (rows || []).find(r => String(r.itemcode).trim().toLowerCase() === String(initialItemcode).trim().toLowerCase());
    if (match) {
      setItemcodeFilter(match.itemcode?.trim() || undefined);
      setSizeFilter(match.size_text?.trim() || undefined);
      setDescFilter((match.description ?? '').trim() || undefined);
      setDescSearch('');
    }
  }, [open, initialItemcode, rows]);

  // When modal closes, clear the invisible itemcode filter so next open can decide fresh
  useEffect(() => {
    if (!open) {
      setItemcodeFilter(undefined);
    }
  }, [open]);

  // Cascading options
  const sizeOptions = useMemo(() => {
    const set = new Set<string>();
    (rows || [])
      .filter(r => (!descFilter || norm(r.description) === norm(descFilter)))
      .forEach(r => { const s = r.size_text?.trim(); if (s) set.add(s); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [rows, descFilter]);

  const descOptions = useMemo(() => {
    const set = new Map<string, string>();
    (rows || [])
      .filter(r => (!sizeFilter || norm(r.size_text) === norm(sizeFilter)))
      .forEach(r => { const d = (r.description ?? '').trim(); if (d) { const k = d.toLowerCase(); if (!set.has(k)) set.set(k, d); } });
    return Array.from(set.values()).sort().map(v => ({ value: v, label: v }));
  }, [rows, sizeFilter]);

  const dataSource = useMemo(() => filtered.map((r, idx) => ({ ...r, key: `${r.itemcode}-${r.size_text}-${idx}` })), [filtered]);

  const columns: ColumnsType<VwQaqcPipeMaterialsRow & { key: string }> = [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 160 },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 100 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
  ];

  const handleOk = () => {
    if (mode === 'basic') {
      const key = selectedRowKeys[0];
      const row = dataSource.find(r => r.key === key);
      if (row) onOk(row.itemcode);
      else onOk('');
    } else {
      const selected = selectedRowKeys
        .map(k => dataSource.find(r => r.key === k))
        .filter((r): r is VwQaqcPipeMaterialsRow & { key: string } => !!r)
        .map(r => ({ itemcode: r.itemcode, size_text: r.size_text, description: r.description ?? null }));
      onOk(selected);
    }
  };

  return (
    <Modal
      open={open}
      title={title || 'Select Pipe Material'}
      onOk={handleOk}
      onCancel={onCancel}
      okText={mode === 'basic' ? 'Select' : 'Add Selected'}
      width={720}
    >
      <Space style={{ marginBottom: 8 }} wrap>
        <Select
          placeholder="Size"
          allowClear
          value={sizeFilter}
          onChange={(v) => setSizeFilter(v)}
          style={{ width: 100 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={sizeOptions}
        />
        <Select
          placeholder="Description"
          allowClear
          value={descFilter}
          onChange={(v) => setDescFilter(v)}
          style={{ width: 480 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => {
            const val = (String(option?.label || '')).toLowerCase();
            const terms = (input || '').toLowerCase().split(/\s+/).filter(Boolean);
            return terms.every(t => val.includes(t));
          }}
          onSearch={(val) => setDescSearch(val)}
          options={descOptions}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.key}
        loading={isLoading}
        size="small"
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
        rowSelection={mode === 'combined' ? {
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
          preserveSelectedRowKeys: true,
        } : undefined}
        onRow={(record) => ({
          onDoubleClick: () => {
            if (mode === 'basic') {
              onOk(record.itemcode);
            } else {
              onOk([{ itemcode: record.itemcode, size_text: record.size_text, description: record.description ?? null }]);
            }
          },
          style: { height: 28 }
        })}
        components={{
          body: {
            row: (props: any) => (
              <tr
                {...props}
                style={{
                  ...props.style,
                  lineHeight: 1,
                  padding: 0,
                  borderBottom: '1px solid #f0f0f0',
                  height: 28,
                }}
              />
            ),
            cell: (props: any) => (
              <td
                {...props}
                style={{
                  ...props.style,
                  padding: '0 8px',
                  borderBottom: 'none',
                  height: 28,
                  verticalAlign: 'middle',
                }}
              />
            ),
          },
        }}
        // Let modal height grow with pagination like ActivitySelections; no vertical scroll here
      />
    </Modal>
  );
};

export default PipeMaterialSelect;
